import React, { useEffect, useState } from "react";
import {
  API_BASE,
  EXEC_APPENDER,
  APP_NAMES,
  PAYMENT_VALIDATOR_URL,
  APP_CONFIG,
} from "../utils/constants";
import { fetchTextWithDiagnostics } from "../utils/helpers";
import { Shell, PrimaryButton, DangerLink } from "../components/UI";
import safeExamIllustration from "../assets/safe-exam.svg";

// =====================
// Shared page shell: nav + footer matching landing page theme
// =====================
function LoginPageWrapper({ isDarkMode, children }) {
  return (
    <Shell>
      <div className={`min-h-screen flex flex-col ${isDarkMode ? "bg-slate-900" : "bg-gradient-to-br from-blue-50 to-indigo-50"}`}>
        <nav className={isDarkMode ? "bg-slate-900 border-b border-slate-700" : "bg-white border-b border-slate-200 shadow-sm"}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <a href="/" className="flex items-center gap-2">
                <img src="/logo-icon.svg" alt="PassMLOExam logo" className="h-10 w-10" />
                <span className={`font-bold text-lg ${isDarkMode ? "text-white" : "text-slate-900"}`}>PassMLOExam</span>
              </a>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium">
                <a href="/" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Home</a>
                <a href="/faq.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>FAQ</a>
                <a href="/resources/index.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Study Guides</a>
                <a href="/mortgage-calculator.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Calculator</a>
                <a href="/contact.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Contact</a>
              </div>
            </div>
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center p-4 py-10">
          {children}
        </div>
        <footer className={`border-t ${isDarkMode ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-white"}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"} text-center space-y-2`}>
              <div>© 2026 PassMLOExam. All rights reserved.</div>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                <a href="/faq.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>FAQ</a>
                <a href="/contact.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Contact Us</a>
                <a href="/privacy.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Privacy</a>
                <a href="/copyright.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Copyright</a>
                <a href="/resources/index.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Study Guides</a>
                <a href="/mortgage-calculator.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Calculator</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Shell>
  );
}

// =====================
// Generic exam login with app selection
// =====================
export function ExamLogin({ onLogin }) {
  // Use runtime hostname first so behavior follows actual URL/domain
  const hostname =
    typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const isExamPracticeAppHost =
    hostname === "exampracticeapp.com" || hostname === "www.exampracticeapp.com";
  const isPassMLOExamHost =
    hostname === "passmloexam.com" || hostname === "www.passmloexam.com";

  // Only show app select for exampracticeapp.com, hardcode for passmloexam.com
  const isExamPracticeApp =
    isExamPracticeAppHost || (!isPassMLOExamHost && APP_CONFIG.domain === "exampracticeapp.com");
  const isPassMLOExam = isPassMLOExamHost || APP_CONFIG.domain === "passmloexam.com";
  const defaultAppName = isPassMLOExam ? APP_CONFIG.name : (localStorage.getItem("appName") || APP_NAMES[0]);
  const [appName, setAppName] = useState(defaultAppName);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter email and password.");
      return;
    }

    setLoading(true);

    const res = await fetchTextWithDiagnostics(`${API_BASE}/users/authenticate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userEmail: email,
        userPassword: password,
        userRole: "User",
        appName: appName,
      }),
    });

    if (res.status === 401 || !res.ok || res.text.trim() === "Invalid login") {
      setError("Invalid email or password.");
      setLoading(false);
      return;
    }

    const bearer = `Bearer ${res.text.trim()}`;
    localStorage.setItem("accessToken", bearer);
    localStorage.setItem("userEmail", email);
    localStorage.setItem("appName", appName);
    setLoading(false);
    console.log("[DEBUG] 🔑 LOGIN SUCCESS - calling onLogin with token");
    onLogin?.(bearer);
  };

  const handleBackToHome = () => {
    localStorage.removeItem("loginMode");
    window.location.hash = "#/";
    window.location.reload();
  };

  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );

  useEffect(() => {
    const updateTheme = () => {
      setIsDarkMode(localStorage.getItem("theme") === "dark");
    };
    window.addEventListener("storage", updateTheme);
    return () => window.removeEventListener("storage", updateTheme);
  }, []);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [isDarkMode]);

  return (
    <LoginPageWrapper isDarkMode={isDarkMode}>
        <div
          className={`w-full max-w-md rounded-2xl border-2 ${
            isDarkMode
              ? "border-slate-700 bg-slate-800 shadow-xl"
              : "border-blue-200 bg-white shadow-2xl"
          } p-8`}
          data-tour="login-form"
        >
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800 transition"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </button>

            <img src="/logo-icon.svg" alt="PassMLOExam logo" className="h-12 w-12" />

            <div className="w-20" />
          </div>

          <div className="flex items-center justify-center mb-6">
            <img
              src={safeExamIllustration}
              alt="Safe exam preparation"
              className="h-24 w-auto"
            />
          </div>

          <h1
            className={`text-3xl font-bold mb-2 text-center ${
              isDarkMode ? "text-white" : "text-slate-900"
            }`}
          >
            {APP_CONFIG.name}
          </h1>

          <p
            className={`text-center text-sm mb-8 ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}
          >
            Exam preparation
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isExamPracticeApp ? (
              <div>
                <label
                  className={`block text-sm font-semibold mb-2 ${
                    isDarkMode ? "text-white" : "text-slate-900"
                  }`}
                >
                  Exam Type
                </label>
                <select
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  className={`w-full rounded-lg border-2 px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${
                    isDarkMode
                      ? "border-slate-600 bg-slate-700 text-white"
                      : "border-slate-200 bg-white text-slate-900"
                  }`}
                >
                  {APP_NAMES.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${
                  isDarkMode ? "text-white" : "text-slate-900"
                }`}
              >
                Email
              </label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={`w-full rounded-lg border-2 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDarkMode
                    ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400"
                    : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${
                  isDarkMode ? "text-white" : "text-slate-900"
                }`}
              >
                Password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                className={`w-full rounded-lg border-2 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDarkMode
                    ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400"
                    : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400"
                }`}
              />
            </div>

            {error && (
              <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3 text-red-700 text-sm font-medium">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white px-4 py-3 font-semibold transition shadow-md hover:shadow-lg"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="text-xs text-slate-500 text-center mt-6 space-y-2">
            <div>Questions? Contact support.</div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <a href="/faq.html" className="hover:text-slate-700">
                FAQ
              </a>
              <a href="/contact.html" className="hover:text-slate-700">
                Contact Us
              </a>
              <a href="/privacy.html" className="hover:text-slate-700">
                Privacy
              </a>
              <a href="/copyright.html" className="hover:text-slate-700">
                Copyright
              </a>
              <a href="/resources/index.html" className="hover:text-slate-700">
                Study Guides
              </a>
              <a href="/mortgage-calculator.html" className="hover:text-slate-700">
                Calculator
              </a>
              <a
                href="https://buydeepdiscount.com"
                className="hover:text-slate-700"
                target="_blank"
                rel="noreferrer"
              >
                BuyDeep
              </a>
            </div>
          </div>
        </div>
    </LoginPageWrapper>
  );
}

// =====================
// NMLS-specific login (pre-set to NMLS Exam Questions)
// =====================
export function NMLSLogin({ onLogin }) {
  const NMLS_APP = "NMLS Exam Questions";

  const [email, setEmail] = useState(
    () => localStorage.getItem("pendingRegistrationEmail") || ""
  );
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    () => localStorage.getItem("postPaymentNotice") || ""
  );
  const [noticeType, setNoticeType] = useState(
    () => localStorage.getItem("postPaymentNoticeType") || "success"
  );
  const [showRegister, setShowRegister] = useState(
    () => localStorage.getItem("showRegister") === "1"
  );
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );

  // IMPORTANT: All hooks MUST be above any conditional return
  useEffect(() => {
    if (showRegister) localStorage.removeItem("showRegister");
  }, [showRegister]);

  useEffect(() => {
    if (successMessage) {
      localStorage.removeItem("postPaymentNotice");
      localStorage.removeItem("postPaymentNoticeType");
    }
  }, [successMessage]);

  useEffect(() => {
    const updateTheme = () => setIsDarkMode(localStorage.getItem("theme") === "dark");
    window.addEventListener("storage", updateTheme);
    return () => window.removeEventListener("storage", updateTheme);
  }, []);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [isDarkMode]);

  if (showRegister) {
    return <NMLSRegistration onBack={() => setShowRegister(false)} />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter email and password.");
      return;
    }

    setLoading(true);

    // Step 1: Authenticate user
    const res = await fetchTextWithDiagnostics(`${API_BASE}/users/authenticate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userEmail: email,
        userPassword: password,
        userRole: "User",
        appName: NMLS_APP,
      }),
    });

    if (res.status === 401 || !res.ok || res.text.trim() === "Invalid login") {
      setError("Invalid email or password.");
      setLoading(false);
      return;
    }

    // Step 2: Validate payment status (fail closed)
    try {
      const paymentRes = await fetch(PAYMENT_VALIDATOR_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId: email,
          appName: NMLS_APP,
        }),
      });

      if (!paymentRes.ok) {
        setError("Unable to validate payment. Please try again in a moment.");
        setLoading(false);
        return;
      }

      const paymentData = await paymentRes.json();

      if (!paymentData.hasPaid) {
        setError(`Payment required. Please purchase access to ${APP_CONFIG.name}.`);
        setLoading(false);
        setShowRegister(true);
        return;
      }

      // Step 2b: Check if plan has expired
      if (paymentData.transaction_date) {
        const transactionDate = new Date(paymentData.transaction_date);
        const planDuration = paymentData.plan_duration || 30;
        const expirationDate = new Date(transactionDate);
        expirationDate.setDate(expirationDate.getDate() + planDuration);

        if (new Date() > expirationDate) {
          setError(
            `Your plan expired on ${expirationDate.toLocaleDateString()}. Please purchase a new plan to continue.`
          );
          setLoading(false);
          setShowRegister(true);
          return;
        }
      }
    } catch (err) {
      console.error("Payment validation error:", err);
      setError("Unable to validate payment. Please try again in a moment.");
      setLoading(false);
      return;
    }

    // Step 3: Login successful and payment verified
    const bearer = `Bearer ${res.text.trim()}`;
    localStorage.setItem("accessToken", bearer);
    localStorage.setItem("userEmail", email);
    localStorage.setItem("appName", NMLS_APP);
    setLoading(false);
    onLogin?.(bearer);
  };

  const handleBackToHome = () => {
    localStorage.removeItem("loginMode");
    window.location.hash = "#/";
    window.location.reload();
  };

  return (
    <LoginPageWrapper isDarkMode={isDarkMode}>
        <div
          className={`w-full max-w-md rounded-2xl border-2 ${
            isDarkMode
              ? "border-slate-700 bg-slate-800 shadow-xl"
              : "border-blue-200 bg-white shadow-2xl"
          } p-8`}
        >
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800 transition"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </button>

            <img src="/logo-icon.svg" alt="PassMLOExam logo" className="h-12 w-12" />

            <div className="w-20" />
          </div>

          <h1
            className={`text-3xl font-bold mb-2 text-center ${
              isDarkMode ? "text-white" : "text-slate-900"
            }`}
          >
            {APP_CONFIG.name}
          </h1>

          <p
            className={`text-center text-sm mb-8 ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}
          >
            Safe exam preparation for the NMLS exam
          </p>

          <div className="mb-8 flex items-center justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700">
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 3l7 4v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V7l7-4z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              Trusted, secure exam access
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {successMessage && (
              <div
                className={`rounded-2xl border p-3 text-sm ${
                  noticeType === "warning"
                    ? "border-amber-200 bg-amber-50 text-amber-800"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800"
                }`}
              >
                {successMessage}
              </div>
            )}

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${
                  isDarkMode ? "text-white" : "text-slate-900"
                }`}
              >
                Email
              </label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (successMessage) {
                    setSuccessMessage("");
                    setNoticeType("success");
                  }
                }}
                placeholder="your@email.com"
                className={`w-full rounded-lg border-2 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDarkMode
                    ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400"
                    : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${
                  isDarkMode ? "text-white" : "text-slate-900"
                }`}
              >
                Password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                className={`w-full rounded-lg border-2 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDarkMode
                    ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400"
                    : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400"
                }`}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white px-4 py-3 font-semibold transition shadow-md hover:shadow-lg"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-slate-500">
                New to {APP_CONFIG.name}?
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowRegister(true)}
            className="w-full px-4 py-3 rounded-lg border-2 border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition"
          >
            Create Account
          </button>

          <div className="text-xs text-slate-500 text-center mt-6 space-y-2">
            <div>Questions? Contact <a href="mailto:support@alerainfotech.com" className="underline hover:text-slate-700">support@alerainfotech.com</a></div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <a href="/faq.html" className="hover:text-slate-700">
                FAQ
              </a>
              <a href="/contact.html" className="hover:text-slate-700">
                Contact Us
              </a>
              <a href="/privacy.html" className="hover:text-slate-700">
                Privacy
              </a>
              <a href="/copyright.html" className="hover:text-slate-700">
                Copyright
              </a>
              <a href="/resources/index.html" className="hover:text-slate-700">
                Study Guides
              </a>
              <a href="/mortgage-calculator.html" className="hover:text-slate-700">
                Calculator
              </a>
              <a
                href="https://buydeepdiscount.com"
                className="hover:text-slate-700"
                target="_blank"
                rel="noreferrer"
              >
                BuyDeep
              </a>
            </div>
          </div>
        </div>
    </LoginPageWrapper>
  );
}

// Backward compatibility alias
export const ExamPracticeLogin = ExamLogin;

// =====================
// NMLS Registration with Stripe Checkout
// =====================
export function NMLSRegistration({ onBack }) {
  const NMLS_APP = "NMLS Exam Questions";
  const TEST_MODE = false;

  const STRIPE_LINK_MONTHLY_TEST =
    "https://buy.stripe.com/test_fZucN63QQ23baFs6L9fnO00";
  const STRIPE_LINK_QUARTERLY_TEST =
    "https://buy.stripe.com/test_14AcN63QQ9vD5l82uTfnO01";

  const STRIPE_LINK_MONTHLY_LIVE = "https://buy.stripe.com/fZucN63QQ23baFs6L9fnO00";
  const STRIPE_LINK_QUARTERLY_LIVE =
    "https://buy.stripe.com/14AcN63QQ9vD5l82uTfnO01";

  const STRIPE_LINK_MONTHLY = TEST_MODE
    ? STRIPE_LINK_MONTHLY_TEST
    : STRIPE_LINK_MONTHLY_LIVE;
  const STRIPE_LINK_QUARTERLY = TEST_MODE
    ? STRIPE_LINK_QUARTERLY_TEST
    : STRIPE_LINK_QUARTERLY_LIVE;

  const [isDarkMode] = useState(localStorage.getItem("theme") === "dark");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(() => {
    const stored = localStorage.getItem("selectedPlan");
    return stored === "quarterly" ? "quarterly" : "monthly";
  });

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [isDarkMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRegisterClick = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !formData.email ||
      !formData.password ||
      !formData.firstName ||
      !formData.lastName
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const registerRes = await fetchTextWithDiagnostics(`${API_BASE}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userPhone: "",
          userPassword: formData.password,
          userRole: "User",
          userEmail: formData.email,
          appName: NMLS_APP,
        }),
      });

      if (!registerRes.ok) {
        const message = registerRes.text?.trim() || "Registration failed.";
        const friendly = /exist|already/i.test(message)
          ? "Email already exists. Please log in instead."
          : message;
        setError(friendly);
        setLoading(false);
        return;
      }

      const redirectUrl =
        selectedPlan === "monthly" ? STRIPE_LINK_MONTHLY : STRIPE_LINK_QUARTERLY;

      const normalizedEmail = formData.email.trim().toLowerCase();
      const checkoutUrl = new URL(redirectUrl);
      checkoutUrl.searchParams.set("prefilled_email", normalizedEmail);
      checkoutUrl.searchParams.set("client_reference_id", normalizedEmail);

      localStorage.setItem("pendingRegistrationEmail", normalizedEmail);
      localStorage.setItem("pendingRegistrationApp", NMLS_APP);
      localStorage.setItem("loginMode", "nmls");

      window.location.href = checkoutUrl.toString();
      return;
    } catch (err) {
      setError(err.message || "Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginPageWrapper isDarkMode={isDarkMode}>
        <div
          className={`w-full max-w-md rounded-2xl border-2 ${
            isDarkMode ? "border-slate-700 bg-slate-800" : "border-blue-200 bg-white"
          } shadow-2xl p-8`}
          data-tour="register-form"
        >
          <div className="flex items-center justify-center mb-8">
            <img src="/logo-icon.svg" alt="PassMLOExam logo" className="h-12 w-12" />
          </div>

          <div className="flex items-center justify-center mb-6">
            <img
              src={safeExamIllustration}
              alt="Safe exam preparation"
              className="h-24 w-auto"
            />
          </div>

          <h1
            className={`text-3xl font-bold ${
              isDarkMode ? "text-white" : "text-slate-900"
            } mb-2 text-center`}
          >
            Create {APP_CONFIG.name} Account
          </h1>

          <p
            className={`${
              isDarkMode ? "text-slate-400" : "text-slate-600"
            } text-center text-sm mb-8`}
          >
            Start your safe exam preparation today
          </p>

          <div className="mb-8 flex items-center justify-center">
            <div
              className={`inline-flex items-center gap-2 rounded-full border ${
                isDarkMode
                  ? "border-blue-900 bg-blue-950 text-blue-300"
                  : "border-blue-200 bg-blue-50 text-blue-700"
              } px-4 py-2 text-xs font-semibold`}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 3l7 4v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V7l7-4z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              Secure checkout and verified access
            </div>
          </div>

          <form onSubmit={handleRegisterClick} className="space-y-4">
            <div>
              <label
                className={`block text-sm font-semibold ${
                  isDarkMode ? "text-slate-300" : "text-slate-900"
                } mb-2`}
              >
                Choose Plan
              </label>

              <div className="grid grid-cols-1 gap-3">
                <label
                  className={`flex items-center justify-between rounded-lg border-2 ${
                    isDarkMode
                      ? "border-slate-600 hover:bg-slate-700"
                      : "border-slate-200 hover:bg-blue-50"
                  } px-4 py-3 cursor-pointer`}
                >
                  <div>
                    <div
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-white" : "text-slate-900"
                      }`}
                    >
                      $50 / access
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Standard plan • 1 Month
                    </div>
                  </div>

                  <input
                    type="radio"
                    name="plan"
                    value="monthly"
                    checked={selectedPlan === "monthly"}
                    onChange={() => setSelectedPlan("monthly")}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>

                <label
                  className={`flex items-center justify-between rounded-lg border-2 ${
                    isDarkMode
                      ? "border-slate-600 hover:bg-slate-700"
                      : "border-slate-200 hover:bg-blue-50"
                  } px-4 py-3 cursor-pointer`}
                >
                  <div>
                    <div
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-white" : "text-slate-900"
                      }`}
                    >
                      $100 / access
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Premium plan • 3 Months
                    </div>
                  </div>

                  <input
                    type="radio"
                    name="plan"
                    value="quarterly"
                    checked={selectedPlan === "quarterly"}
                    onChange={() => setSelectedPlan("quarterly")}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className={`block text-sm font-semibold ${
                    isDarkMode ? "text-slate-300" : "text-slate-900"
                  } mb-2`}
                >
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="John"
                  className={`w-full rounded-lg border-2 ${
                    isDarkMode
                      ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400 focus:ring-blue-400"
                      : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400 focus:ring-blue-400"
                  } px-4 py-3 text-sm focus:outline-none focus:ring-2`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-semibold ${
                    isDarkMode ? "text-slate-300" : "text-slate-900"
                  } mb-2`}
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Doe"
                  className={`w-full rounded-lg border-2 ${
                    isDarkMode
                      ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400 focus:ring-blue-400"
                      : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400 focus:ring-blue-400"
                  } px-4 py-3 text-sm focus:outline-none focus:ring-2`}
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-semibold ${
                  isDarkMode ? "text-slate-300" : "text-slate-900"
                } mb-2`}
              >
                Email *
              </label>
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                className={`w-full rounded-lg border-2 ${
                  isDarkMode
                    ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400 focus:ring-blue-400"
                    : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400 focus:ring-blue-400"
                } px-4 py-3 text-sm focus:outline-none focus:ring-2`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-semibold ${
                  isDarkMode ? "text-slate-300" : "text-slate-900"
                } mb-2`}
              >
                Password *
              </label>
              <input
                type="password"
                name="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Min. 6 characters"
                className={`w-full rounded-lg border-2 ${
                  isDarkMode
                    ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400 focus:ring-blue-400"
                    : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400 focus:ring-blue-400"
                } px-4 py-3 text-sm focus:outline-none focus:ring-2`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-semibold ${
                  isDarkMode ? "text-slate-300" : "text-slate-900"
                } mb-2`}
              >
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="••••••"
                className={`w-full rounded-lg border-2 ${
                  isDarkMode
                    ? "border-slate-600 bg-slate-700 text-white caret-white placeholder-slate-400 focus:ring-blue-400"
                    : "border-slate-200 bg-white text-slate-900 caret-slate-900 placeholder-slate-400 focus:ring-blue-400"
                } px-4 py-3 text-sm focus:outline-none focus:ring-2`}
              />
            </div>

            {error && (
              <div
                className={`rounded-2xl border ${
                  isDarkMode
                    ? "border-red-900 bg-red-950 text-red-300"
                    : "border-red-200 bg-red-50 text-red-700"
                } p-3 text-sm`}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white px-4 py-3 font-semibold transition shadow-md hover:shadow-lg"
            >
              {loading ? "Redirecting to Stripe..." : "Continue to Payment"}
            </button>
          </form>

          <button
            type="button"
            onClick={onBack}
            className={`w-full mt-3 px-4 py-3 rounded-lg border-2 font-semibold ${
              isDarkMode
                ? "border-slate-600 text-slate-300 hover:bg-slate-700"
                : "border-blue-200 text-blue-600 hover:bg-blue-50"
            }`}
          >
            Back to Login
          </button>

          <div
            className={`text-xs ${
              isDarkMode ? "text-slate-400" : "text-slate-500"
            } text-center mt-6 space-y-2`}
          >
            <div>You will be redirected to Stripe to complete payment.</div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <a
                href="/faq.html"
                className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}
              >
                FAQ
              </a>
              <a
                href="/contact.html"
                className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}
              >
                Contact Us
              </a>
              <a
                href="/privacy.html"
                className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}
              >
                Privacy
              </a>
              <a
                href="/copyright.html"
                className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}
              >
                Copyright
              </a>
              <a
                href="/resources/index.html"
                className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}
              >
                Study Guides
              </a>
              <a
                href="/mortgage-calculator.html"
                className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}
              >
                Calculator
              </a>
              <a
                href="https://buydeepdiscount.com"
                className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}
                target="_blank"
                rel="noreferrer"
              >
                BuyDeep
              </a>
            </div>
          </div>
        </div>
    </LoginPageWrapper>
  );
}